import { useId, useState } from 'react'
import type { ReactNode, RefObject } from 'react'
import { createPortal } from 'react-dom'
import type { components } from '../../lib/api/schema.gen'
import { formatMoney, multiplyAmount } from '../../verticals/cart/cart-money'
import {
  VolumeBreaksList,
  VolumeCurveChart,
} from '../../verticals/parts/components/VolumeCurveVisual'
import { Button } from './Button'
import { useAnchoredPopover } from './useAnchoredPopover'
import { useFocusTrap } from './useFocusTrap'

export type VolumePricingCurve = components['schemas']['VolumePricingCurve']
export type VolumePricingTier = components['schemas']['VolumePricingTier']

/**
 * The tier that applies to a requested quantity — same rule as the parts
 * vertical's `findApplicableTier` (`verticals/parts/volume-pricing.ts`),
 * duplicated here on purpose: the shell must not import anything
 * parts-vertical-specific (ADR-0077 §D2), and this pure helper is small
 * enough that duplicating it is safer than reaching across the vertical
 * boundary for it.
 */
function findApplicableTier(
  tiers: readonly VolumePricingTier[],
  qty: number,
): VolumePricingTier | undefined {
  if (qty < 1) {
    return tiers[0]
  }
  let applicable: VolumePricingTier | undefined
  for (const tier of tiers) {
    if (tier.minQuantity <= qty) {
      applicable = tier
    }
  }
  return applicable ?? tiers[0]
}

/** Whether `qty` is beyond the curve's auto-price ceiling (ADR-0058 §3). */
function isAboveCap(curve: VolumePricingCurve, qty: number): boolean {
  return qty > curve.cap
}

/** `unitPrice × qty`, FE-derived display value only (ADR-0058 §1). */
function computeTierTotal(unitPriceAmount: string, qty: number): string {
  return multiplyAmount(unitPriceAmount, qty)
}

export interface VolumePricingPopoverShellRenderTriggerArgs {
  readonly ref: RefObject<HTMLButtonElement | null>
  readonly open: boolean
  readonly toggle: () => void
  readonly ariaHasPopup: 'dialog'
  readonly ariaExpanded: boolean
}

export interface VolumePricingPopoverShellProps {
  /** Heading copy — injected so the shell hardcodes no vertical's wording. */
  readonly heading: string
  readonly quantity: number
  readonly currency: string
  readonly curve: VolumePricingCurve
  /** The trigger surface the caller renders (a Qty-cell button, an RFQ
   * "Volume" chip, etc). The shell owns the popover/dialog wiring but NOT
   * the trigger's look. */
  readonly renderTrigger: (args: VolumePricingPopoverShellRenderTriggerArgs) => ReactNode
  /** Add action — injected. Below-cap only. Resolves/rejects drive the
   * shell's own in-flight/disabled state and success/failure handling. */
  readonly onAdd: () => Promise<void>
  /** Above-cap affordance — injected. The shell mints no price above cap
   * (ADR-0058 §3) and renders this affordance instead. */
  readonly onRequestRfq: () => void
  /** Called once an `onAdd` call resolves successfully, before the popover
   * closes — e.g. to show a success toast. Optional so a caller with no
   * side effect to run (only the shell-seam tests, typically) can omit it. */
  readonly onAddSuccess?: () => void
  /** Called when an `onAdd` call rejects. The popover stays open. */
  readonly onAddError?: (error: unknown) => void
}

/**
 * Shared, vertical-agnostic volume-pricing popover shell (ADR-0077 §D2/§D3,
 * realizing ADR-0064 §D3). Owns the `useAnchoredPopover` + `useFocusTrap`
 * anchoring/focus wiring, the `role="dialog"` portal panel, the
 * below-cap price block + curve visuals, and the add/RFQ CTA wiring —
 * moved verbatim out of the parts vertical's `VolumePricingCellTrigger`,
 * with the add + above-cap-RFQ actions INJECTED as callbacks (Strategy /
 * dependency-inversion at the vertical boundary) instead of hardwired to
 * either vertical's add path.
 *
 * Deliberately imports NOTHING parts- or RFQ-specific: no
 * `LibrarySourceable`, no `addLibraryPartToCart`, no parts `../types`, no
 * parts `../volume-pricing`. `VolumeCurveChart`/`VolumeBreaksList` are
 * reused as-is (pure, display-only) per ADR-0077 §D2.
 *
 * The above-cap boundary (no client-minted price, ADR-0058 §3) is
 * preserved: above `curve.cap` the shell renders the RFQ affordance only
 * and never computes/derives a price.
 */
export function VolumePricingPopoverShell({
  heading,
  quantity,
  currency,
  curve,
  renderTrigger,
  onAdd,
  onRequestRfq,
  onAddSuccess,
  onAddError,
}: VolumePricingPopoverShellProps) {
  const [open, setOpen] = useState(false)
  const { triggerRef, panelRef, position } = useAnchoredPopover(open, setOpen)
  const [activeMinQuantity, setActiveMinQuantity] = useState<number | null>(null)
  const [adding, setAdding] = useState(false)
  const headingId = useId()

  useFocusTrap(panelRef, open, triggerRef)

  const aboveCap = isAboveCap(curve, quantity)
  const applicableTier = aboveCap ? undefined : findApplicableTier(curve.tiers, quantity)
  const total = applicableTier ? computeTierTotal(applicableTier.unitPriceAmount, quantity) : null

  async function handleAdd() {
    if (adding) return
    setAdding(true)
    try {
      await onAdd()
      onAddSuccess?.()
      setOpen(false)
    } catch (error) {
      onAddError?.(error)
    } finally {
      setAdding(false)
    }
  }

  return (
    <>
      {renderTrigger({
        ref: triggerRef,
        open,
        toggle: () => setOpen((current) => !current),
        ariaHasPopup: 'dialog',
        ariaExpanded: open,
      })}
      {open &&
        position &&
        createPortal(
          <div
            ref={panelRef}
            // biome-ignore lint/a11y/useSemanticElements: matches Modal.tsx's
            // hand-rolled dialog pattern (no native <dialog> here either) so
            // the same focus-trap/Escape/outside-click shape applies.
            role="dialog"
            aria-modal="true"
            aria-labelledby={headingId}
            className="volume-pricing-popover-panel"
            style={{ top: position.top, right: position.right }}
          >
            <div className="volume-pricing-popover-header">
              <span id={headingId} className="volume-pricing-popover-title">
                {heading}
              </span>
              <span className="caption-text">Auto-priced to {curve.cap} pcs</span>
            </div>
            {aboveCap ? (
              <p className="caption-text volume-curve-panel-caption">
                Above {curve.cap} pcs we can't auto-price — request an RFQ for committed volume
                pricing.
              </p>
            ) : (
              applicableTier && (
                <div className="volume-pricing-popover-price">
                  <span className="volume-pricing-popover-unit">
                    {formatMoney(applicableTier.unitPriceAmount, currency)}
                  </span>
                  <span className="caption-text">
                    /ea{total ? ` · ${formatMoney(total, currency)} for ${quantity} pcs` : ''}
                  </span>
                </div>
              )
            )}
            <VolumeCurveChart curve={curve} activeMinQuantity={activeMinQuantity} />
            <VolumeBreaksList
              curve={curve}
              currency={currency}
              activeMinQuantity={activeMinQuantity}
              onActiveChange={setActiveMinQuantity}
            />
            {aboveCap ? (
              <Button
                variant="outline-brand"
                size="sm"
                onClick={onRequestRfq}
                className="volume-pricing-popover-cta"
              >
                Request volume RFQ
              </Button>
            ) : (
              <Button
                variant="primary"
                size="sm"
                loading={adding}
                onClick={handleAdd}
                className="volume-pricing-popover-cta"
              >
                Add {quantity} to cart
              </Button>
            )}
          </div>,
          document.body,
        )}
    </>
  )
}
